/**
 * Nhãn cho bảng phân quyền theo tài khoản.
 *
 * Tên menu KHÔNG lặp lại ở đây — bảng dùng thẳng `nav.*`, để một mục đổi tên
 * trên sidebar thì bảng phân quyền đổi theo, không lệch nhau.
 */
export const userAccessEn = {
  title: "Permissions",
  open: "Permissions",
  mode: "How this account gets its permissions",
  modeRole: "Follow the role",
  modeRoleHint: "Uses the standard table for {{role}}. This is how every existing account works today.",
  modeCustom: "Choose each screen",
  modeCustomHint: "Only the screens ticked below, and only the actions ticked next to them.",
  adminNote: "An administrator always has full access, so there is nothing to tick. Change the role first to give this account a permission table.",
  selfNote: "You cannot put your own account on a custom permission table.",
  screens: "Screens and actions",
  screensHint: "Nothing ticked means no access. The server refuses the address too, not just the menu.",
  verbs: { view: "View", create: "Create", update: "Edit", delete: "Delete", approve: "Approve" },
  approveHint:
    "Approve only matters where a document has a real approval step: confirming a purchase or sales order, posting a stock count, posting a journal entry, approving an IQC result, confirming a receipt or lot transfer. Edit lets someone prepare and send a draft; Approve lets them make it effective.",
  useTemplate: "Start from a job template…",
  templates: {
    generalManager: "General manager / management",
    purchasingSupervisor: "Purchasing supervisor",
    purchaser: "Purchaser",
    salesSupervisor: "Sales supervisor",
    orderFollower: "Sales / order follower",
    finance: "Finance / accountant",
    warehouseKeeper: "Warehouse keeper",
    qualityInspector: "Quality inspector",
    viewer: "View only",
  },
  all: "All",
  none: "None",
  usePreset: "Start from the {{role}} table",
  cost: "May see cost and profit",
  costHint: "Purchase cost, margin and the profit report. Independent of the screens above.",
  scopedBadge: "By customer",
  customers: "Customers this account may see",
  customerAll: "Every customer",
  customerAllHint: "For finance, warehouse and managers. With a data region set, every customer of that region.",
  customerAssigned: "Only customers this person follows",
  customerAssignedHint: "Order followers are chosen on each customer (the Order followers field) or ticked below — both edit the same list. Documents of any other customer are hidden everywhere, including a direct link. A customer with no order follower is visible only to administrators and to accounts that see every customer. Following no customer means seeing no customer document.",
  customerFollowedList: "Customers this person follows",
  customerSearch: "Search customers",
  customersError: "Could not load the customer list.",
  customerSelected: "{{count}} chosen",
  privateSuppliers: "Private suppliers this account may see",
  privateSuppliersHint: "A private supplier is visible only to administrators and to the people ticked here. It is the same list as the Followers field on the supplier. Regular suppliers need no tick: the whole team sees them.",
  privateSuppliersNone: "There is no private supplier yet.",
  privateSuppliersError: "Could not load the supplier list.",
  save: "Save permissions",
  saved: "Permissions saved",
  signOutWarning: "Saving signs this account out, so the new permissions take effect immediately.",
  exportNote:
    "Printing and CSV export are built in the browser from what is already on screen, so they follow View — there is no separate tick for them.",
  summaryRole: "Role table",
  summaryCustom: "{{count}} screens",
  region: "Data region",
  regionHint: "Which team's data this account works with. It applies in both modes above; an administrator always sees both regions.",
  regionAll: "Both regions",
  regionVietnam: "Vietnam only",
  regionChina: "China only",
  regionOneHint:
    "A customer belongs to the region set in its Class (where the customer's factory is). This account sees only customers of this region and every document tied to them: quotations, sales orders, PIs, invoices, packing lists, delivery notes, statements, samples, after-sales, the dashboard, sales analytics and customer prices on a product. Suppliers have a region too: this account sees its region's suppliers and the unclassified ones, with their purchase requisitions, RFQs, purchase orders, supplier claims and supplier sample orders. Products themselves are shared by both regions. Customers and suppliers this account creates belong to its region.",
  regionPending: "Awaiting customer confirmation: ",
  regionPendingHint:
    "Not split by region yet: warehouse & LOT, supplier invoices and payments, production, quality and the ledger. This account sees them for both regions — untick those screens if it must not.",
  regionBadge: "Region: {{region}}",
  adminNoRegion: "An administrator always sees both regions. Change the role first to limit this account to one region.",
};

export const userAccessVi = {
  title: "Phân quyền",
  open: "Phân quyền",
  mode: "Tài khoản này lấy quyền theo cách nào",
  modeRole: "Theo vai trò",
  modeRoleHint: "Dùng bảng quyền chuẩn của {{role}}. Mọi tài khoản hiện có đang chạy như vậy.",
  modeCustom: "Chọn từng màn hình",
  modeCustomHint: "Chỉ những màn hình được tick dưới đây, và chỉ những hành động được tick bên cạnh.",
  adminNote: "Quản trị viên luôn có toàn quyền nên không có gì để tick. Muốn giao bảng quyền riêng thì đổi vai trò trước.",
  selfNote: "Không thể đưa chính tài khoản của bạn sang bảng quyền riêng.",
  screens: "Màn hình và hành động",
  screensHint: "Không tick gì là không vào được. Máy chủ chặn cả khi gõ thẳng đường dẫn, không chỉ ẩn menu.",
  verbs: { view: "Xem", create: "Tạo", update: "Sửa", delete: "Xóa", approve: "Duyệt" },
  approveHint:
    "Ô Duyệt chỉ có tác dụng ở chứng từ có bước duyệt thật: xác nhận đơn mua / đơn bán, áp số kiểm kê, ghi sổ bút toán, duyệt kết quả IQC, xác nhận phiếu nhập kho hay chuyển lô. Sửa là được lập và gửi nháp; Duyệt là được làm cho chứng từ có hiệu lực.",
  useTemplate: "Lấy sẵn mẫu theo vị trí…",
  templates: {
    generalManager: "Ban giám đốc / quản lý",
    purchasingSupervisor: "Trưởng phòng mua hàng",
    purchaser: "Nhân viên mua hàng",
    salesSupervisor: "Trưởng phòng kinh doanh",
    orderFollower: "Nhân viên kinh doanh / theo đơn",
    finance: "Tài chính / kế toán",
    warehouseKeeper: "Thủ kho",
    qualityInspector: "Kiểm tra chất lượng",
    viewer: "Chỉ xem",
  },
  all: "Tất cả",
  none: "Bỏ hết",
  usePreset: "Lấy sẵn bảng của {{role}}",
  cost: "Được xem giá vốn và lợi nhuận",
  costHint: "Giá mua, lãi và báo cáo lợi nhuận. Không phụ thuộc các màn hình ở trên.",
  scopedBadge: "Theo khách",
  customers: "Tài khoản này được xem khách nào",
  customerAll: "Tất cả khách hàng",
  customerAllHint: "Cho kế toán, kho, quản lý. Có khu vực dữ liệu thì là mọi khách của khu vực đó.",
  customerAssigned: "Chỉ khách mình theo đơn",
  customerAssignedHint: "Người theo đơn chọn ở hồ sơ từng khách (ô Người theo đơn) hoặc tick ở danh sách dưới — hai chỗ là một danh sách. Chứng từ của khách khác bị ẩn ở mọi nơi, kể cả khi có đường dẫn trực tiếp. Khách chưa có người theo đơn chỉ quản trị viên và tài khoản xem tất cả khách thấy. Chưa theo đơn khách nào thì không thấy chứng từ khách nào.",
  customerFollowedList: "Khách người này theo đơn",
  customerSearch: "Tìm khách hàng",
  customersError: "Không tải được danh sách khách hàng.",
  customerSelected: "Đã chọn {{count}}",
  privateSuppliers: "Nhà cung cấp riêng tư được xem",
  privateSuppliersHint: "Nhà cung cấp riêng tư chỉ quản trị viên và người được tick ở đây thấy. Cùng danh sách với ô Người theo dõi trên hồ sơ nhà cung cấp. Nhà cung cấp thường không cần tick: cả đội đều thấy.",
  privateSuppliersNone: "Chưa có nhà cung cấp riêng tư nào.",
  privateSuppliersError: "Không tải được danh sách nhà cung cấp.",
  save: "Lưu phân quyền",
  saved: "Đã lưu phân quyền",
  signOutWarning: "Lưu xong sẽ đăng xuất tài khoản đó để quyền mới có hiệu lực ngay.",
  exportNote:
    "Bản in và tệp CSV được dựng trong trình duyệt từ dữ liệu đang hiển thị, nên chúng đi theo quyền Xem — không có ô tick riêng.",
  summaryRole: "Theo vai trò",
  summaryCustom: "{{count}} màn hình",
  region: "Khu vực dữ liệu",
  regionHint: "Tài khoản này làm việc với dữ liệu của đội nào. Áp cho cả hai cách lấy quyền ở trên; quản trị viên luôn thấy cả hai khu vực.",
  regionAll: "Cả hai khu vực",
  regionVietnam: "Chỉ Việt Nam",
  regionChina: "Chỉ Trung Quốc",
  regionOneHint:
    "Khách thuộc khu vực ghi ở ô Phân loại của khách (xưởng của khách đặt ở đâu). Tài khoản này chỉ thấy khách của khu vực này và mọi chứng từ gắn với họ: báo giá, đơn bán hàng, PI, hóa đơn, phiếu đóng gói, phiếu giao hàng, công nợ, hàng mẫu, hậu mãi, bảng điều khiển, phân tích bán hàng và giá theo khách trên sản phẩm. Nhà cung cấp cũng có khu vực: tài khoản này thấy nhà cung cấp của khu vực mình và nhà cung cấp chưa phân loại, cùng yêu cầu mua, yêu cầu báo giá, đơn mua, khiếu nại nhà cung cấp và đơn mẫu lấy từ xưởng. Bản thân sản phẩm dùng chung cho cả hai khu vực. Khách và nhà cung cấp do tài khoản này tạo thuộc khu vực của nó.",
  regionPending: "Chờ khách xác nhận: ",
  regionPendingHint:
    "Chưa tách theo khu vực: kho & LOT, hoá đơn và thanh toán nhà cung cấp, sản xuất, chất lượng và sổ cái. Tài khoản này vẫn thấy phần đó của cả hai khu vực — bỏ tick các màn hình đó nếu không được xem.",
  regionBadge: "Khu vực: {{region}}",
  adminNoRegion: "Quản trị viên luôn thấy cả hai khu vực. Muốn giới hạn tài khoản này vào một khu vực thì đổi vai trò trước.",
};

export const userAccessZh = {
  title: "权限设置",
  open: "权限设置",
  mode: "此账号如何取得权限",
  modeRole: "依角色",
  modeRoleHint: "采用 {{role}} 的标准权限表。目前所有既有账号都是这样运作。",
  modeCustom: "逐一勾选页面",
  modeCustomHint: "只开放下方勾选的页面，并且只开放旁边勾选的操作。",
  adminNote: "管理员一律拥有完整权限，因此没有可勾选的项目。要给予自定义权限表，请先变更角色。",
  selfNote: "无法将自己的账号改为自定义权限表。",
  screens: "页面与操作",
  screensHint: "未勾选即无法进入。服务器连直接输入网址也会拒绝，不只是隐藏菜单。",
  verbs: { view: "查看", create: "创建", update: "编辑", delete: "删除", approve: "审核" },
  approveHint:
    "“审核”只在有真正审核步骤的单据上生效：确认采购单／销售订单、过账盘点、过账分录、审核 IQC 结果、确认入库单或批次调拨。“编辑”可创建并提交草稿；“审核”可让单据正式生效。",
  useTemplate: "应用职位模板…",
  templates: {
    generalManager: "总经理／管理层",
    purchasingSupervisor: "采购主管",
    purchaser: "采购员",
    salesSupervisor: "业务主管",
    orderFollower: "业务／跟单员",
    finance: "财务／会计",
    warehouseKeeper: "仓管员",
    qualityInspector: "质检员",
    viewer: "仅查看",
  },
  all: "全选",
  none: "取消全选",
  usePreset: "应用 {{role}} 的权限表",
  cost: "可查看成本与利润",
  costHint: "采购成本、毛利与利润报表。与上方页面权限无关。",
  scopedBadge: "依客户",
  customers: "此账号可查看的客户",
  customerAll: "所有客户",
  customerAllHint: "适用于财务、仓库和主管。如设置了数据区域，即为该区域的全部客户。",
  customerAssigned: "仅限自己跟单的客户",
  customerAssignedHint: "跟单员可在每个客户资料中选择（“跟单员”栏），也可在下方勾选——两处是同一份名单。其他客户的单据一律隐藏，即使拿到直接链接也一样。尚未指定跟单员的客户，只有管理员和可查看所有客户的账号能看到。未跟任何客户则看不到任何客户单据。",
  customerFollowedList: "此人跟单的客户",
  customerSearch: "搜索客户",
  customersError: "无法加载客户列表。",
  customerSelected: "已选 {{count}}",
  privateSuppliers: "此账号可查看的私密供应商",
  privateSuppliersHint: "私密供应商只对管理员和此处勾选的人可见。与供应商档案上的「跟进人」是同一份名单。普通供应商无需勾选：整个团队都能看到。",
  privateSuppliersNone: "暂无私密供应商。",
  privateSuppliersError: "无法加载供应商列表。",
  save: "保存权限",
  saved: "权限已保存",
  signOutWarning: "保存后会将该账号强制退出登录，使新权限立即生效。",
  exportNote: "打印与 CSV 导出是在浏览器以页面上既有的数据生成，因此随“查看”权限而定，没有独立的勾选项目。",
  summaryRole: "依角色",
  summaryCustom: "{{count}} 个页面",
  region: "数据区域",
  regionHint: "此账号处理哪个团队的数据。对上方两种权限方式都生效；管理员始终可查看两个区域。",
  regionAll: "两个区域",
  regionVietnam: "仅越南",
  regionChina: "仅中国",
  regionOneHint:
    "客户所属区域以客户的“类别”为准（客户工厂所在地）。此账号只能查看该区域的客户及其全部相关单据：报价单、销售订单、形式发票、发票、装箱单、送货单、对账单、样品、售后、仪表板、销售分析以及产品上的客户价格。供应商也有区域：此账号可查看本区域及尚未分类的供应商，以及相关的采购申请、询价单、采购订单、供应商索赔和供应商样品单。产品本身由两个区域共用。此账号新建的客户和供应商属于其所在区域。",
  regionPending: "待客户确认：",
  regionPendingHint: "以下尚未按区域划分：仓库与批次、供应商发票与付款、生产、质量和总账。此账号仍可查看两个区域的这些数据——如不应查看，请取消勾选这些页面。",
  regionBadge: "区域：{{region}}",
  adminNoRegion: "管理员始终可查看两个区域。如要将此账号限制在一个区域，请先变更角色。",
};
